import React, { useState } from 'react';
import CreateOrChangeTariffForm from '../../features/tariff/CreateOrChangeTariffForm';
import {
  useCreateNewTariffMutation,
  useUpdateTariffLimitsMutation,
} from '../../features/tariff/tariff.service';
import { isFetchBaseQueryError } from '../../utils/helpers';
import {
  NewTariff,
  TariffIntegrationLimitType,
  TariffToolLimitsType,
  TariffAiLimitType,
} from '../../features/tariff/tariff.types';
import { toast } from 'react-toastify';
import routes from '../../routes';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import cls from './NewTariffPage.module.scss';
import Breadcrumbs from '../../UIComponents/Breadcrumbs';
import { Tab, Tabs } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import SetTariffLimitsTab from '../../features/tariff/SetTariffLimitsTab';

const NewTariffPage = () => {
  const [createNewTariff] = useCreateNewTariffMutation();
  const [activeKey, setActiveKey] = useState<string>('INFO');
  const [updateLimits] = useUpdateTariffLimitsMutation();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [tariffId, setTariffId] = useState<string>('');
  const [integrationsLimits, setIntegrationsLimits] = useState<TariffIntegrationLimitType[]>([]);
  const [toolsLimits, setToolsLimits] = useState<TariffToolLimitsType[]>([]);
  const [aiData, setAiData] = useState<TariffAiLimitType[]>([]);
  const navigate = useNavigate();

  const initialValues = {
    name: '',
    type: '',
    cost: 0,
    refundable: false,
    convertable: false,
    description: '',
    renewable: false,
    periodType: '',
    periodAmount: 0,
    periodExceedHours: 0,
    label: '',
    teaser: '',
    imageSmall: undefined,
    imageBig: undefined,
    availableAiProviders: [],
    topupDiscountPercent: 0,
    referrerBonusTokens: 0,
    inviteeBonusTokens: 0,
    stripeProductId: '',
    stripePriceId: '',
  };

  const [text, setText] = useState<string>('');

  const handleSubmit = async (values: NewTariff) => {
    const { cost, imageSmall, imageBig, stripeProductId, stripePriceId, ...restValues } = values;
    setDisabled(true);
    try {
      const tariffData = {
        ...restValues,
        cost: cost * 100,
        description: text,
        label: values.label ? values.label : null,
        ...(imageSmall && { imageSmall }),
        ...(imageBig && { imageBig }),
        // a tariff is created unbound from Stripe rather than bound to an empty id
        ...(stripeProductId && { stripeProductId }),
        ...(stripePriceId && { stripePriceId }),
      };

      const result = await createNewTariff(tariffData as NewTariff).unwrap();
      if (result.id) {
        setTariffId(result.id);
        setIntegrationsLimits(result.tariffIntegrationLimits);
        setToolsLimits(result.tariffToolLimits);
        setAiData(result.tariffAiLimits ?? []);
        setActiveKey('LIMITS');
      }
    } catch (error) {
      if (isFetchBaseQueryError(error)) {
        setDisabled(false);
        if (error.status === 400) {
          toast.error('Creation failed');
        }
        if (error.status === 401) {
          toast.error('Invalid data');
        }
        if (error.status === 403) {
          toast.error('Forbidden');
        }
      }
    } finally {
      setDisabled(false);
    }
  };

  const updateLimitsHandler = async () => {
    const updateIntegrations = integrationsLimits.reduce((acc, item) => {
      return {
        ...acc,
        [item.id]: item.limit,
      };
    }, {});
    const updateTools = toolsLimits.reduce((acc, item) => {
      return {
        ...acc,
        [item.id]: item.included,
      };
    }, {});
    const updateAiLimits = aiData.reduce((acc, item) => {
      return {
        ...acc,
        [item.aiProvider]: item.limit,
      };
    }, {});

    const limits = {
      tariffIntegrationLimits: updateIntegrations,
      tariffToolLimits: updateTools,
      tariffAiLimits: updateAiLimits,
    };

    try {
      await updateLimits({
        id: tariffId,
        limits,
      }).unwrap();
      navigate(routes.pages.tariffsPage());
      toast.success('Limits updated successfully');
    } catch (error) {
      if (isFetchBaseQueryError(error)) {
        if (error.status === 400) {
          toast.error('Creation failed');
        }
        if (error.status === 403) {
          toast.error('Forbidden');
        }
        if (error.status === 404) {
          toast.error('Tariff not found');
        }
      }
    }
  };

  useDocumentTitle('Create new tariff');

  return (
    <div data-testid="tariffs-container" className={`${cls.Container} grid-container`}>
      <Breadcrumbs
        elements={[
          { name: 'Tariffs', link: routes.pages.tariffsPage(), active: false },
          { name: 'Add tariff', link: '', active: true },
        ]}
        className={cls.BreadCrumbs}
      />
      <h2 className={`${cls.Title} title-1`}>Create new tariff</h2>
      <div className={cls.Content}>
        <Tabs
          fill={false}
          defaultActiveKey={activeKey}
          variant="pills"
          onSelect={(key) => setActiveKey(key as string)}
          className={cls.Tabs}
          key={activeKey}
        >
          <Tab eventKey="INFO" title="Information" className={cls.Tab} disabled={tariffId !== ''}>
            <CreateOrChangeTariffForm
              initialValues={initialValues}
              disabled={disabled}
              handleSubmit={handleSubmit}
              isNew={true}
              setText={setText}
              text={text}
              setDisabled={setDisabled}
            />
          </Tab>
          <Tab eventKey="LIMITS" title="Limits" className={cls.Tab} disabled={tariffId === ''}>
            <SetTariffLimitsTab
              integrations={integrationsLimits}
              tools={toolsLimits}
              aiLimits={aiData}
              updateLimitsHandler={updateLimitsHandler}
              setIntegrationsLimits={setIntegrationsLimits}
              setToolsLimits={setToolsLimits}
              setAiLimits={setAiData}
              isNew={true}
            />
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default NewTariffPage;
